import React, { useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import {
  Box, Button, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack,
  TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  CheckRounded, CloseRounded, EditRounded as EditIcon, RestartAltRounded as RestoreIcon,
} from '@mui/icons-material';
import { httpClient } from '@/utils/httpClient';
import { OPERATION_TYPES } from '@/config/constants';
import {
  BlockHeading, DataTable, DatagridEmpty, HelpDialog, PageTitle, RowActionsMenu, SectionHeader,
} from '@/components/shell';
import { useStatFormat } from '@/features/statistics/accountHealth';
import { CostCenterSelect } from '@/features/credits/CostCenterSelect';

interface Pricing {
  id: number | string;
  operationType: number;
  creditsPerOperation: number;
}

type OperationType = (typeof OPERATION_TYPES)[number];

/** The rate's origin repeats what the credits cell already says ("(default)"), so a phone drops
 *  the column and keeps the row actions in view. */
const STATE_COLUMN = { display: { xs: 'none', sm: 'table-cell' } };

const PricingHelp: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const t = useTranslate();
  const fmt = useStatFormat();
  return (
    <HelpDialog open={open} onClose={onClose} title={t('screens.pricing.helpTitle')}>
      <Typography variant="body2">{t('screens.pricing.helpIntro')}</Typography>
      <Box>
        <Typography variant="subtitle2">{t('screens.pricing.operationTypes')}</Typography>
        <Stack spacing={1} sx={{ mt: 1 }}>
          {OPERATION_TYPES.map(op => (
            <Box key={op.id}>
              <Typography variant="body2">
                {t(`screens.pricing.operations.${op.name}.name`)} · {t('screens.pricing.credits', { smart_count: op.defaultCredits, count: fmt.count(op.defaultCredits) })}
              </Typography>
              <Typography variant="body2" color="text.secondary">{t(`screens.pricing.operations.${op.name}.description`)}</Typography>
            </Box>
          ))}
        </Stack>
      </Box>
      <Typography variant="body2" color="text.secondary">{t('screens.pricing.helpDefaults')}</Typography>
    </HelpDialog>
  );
};

export const PricingPage = () => {
  const t = useTranslate();
  const notify = useNotify();
  const fmt = useStatFormat();
  const [selectedCC, setSelectedCC] = useState<string>('');
  const [pricings, setPricings] = useState<Pricing[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);
  const [restoring, setRestoring] = useState<{ pricing: Pricing; op: OperationType } | null>(null);

  useEffect(() => { if (selectedCC) loadPricings(); }, [selectedCC]);

  const loadPricings = async () => {
    try {
      const r = await httpClient.get<Pricing[]>(`/admin/pricing/GetPricings/${selectedCC}`);
      setPricings(r.data);
    } catch { setPricings([]); }
  };

  const getPricing = (opType: number) => pricings.find(p => p.operationType === opType);

  const handleSave = async (opType: number) => {
    const existing = getPricing(opType);
    try {
      if (existing) {
        await httpClient.put('/admin/pricing/UpdatePricing', { ...existing, creditsPerOperation: parseFloat(editValue) });
      } else {
        await httpClient.post('/admin/pricing/AddPricing', { costCenterId: selectedCC, operationType: opType, creditsPerOperation: parseFloat(editValue) });
      }
      notify('screens.pricing.saved');
      setEditingId(null);
      loadPricings();
    } catch { notify('screens.pricing.saveFailed', { type: 'error' }); }
  };

  const handleRestore = async (pricing: Pricing) => {
    try {
      await httpClient.delete(`/admin/pricing/DeletePricing/${pricing.id}`);
      notify('screens.pricing.restored');
      loadPricings();
    } catch { notify('screens.pricing.restoreFailed', { type: 'error' }); }
    setRestoring(null);
  };

  const startEdit = (op: OperationType) => {
    const pricing = getPricing(op.id);
    setEditingId(op.id);
    setEditValue(pricing?.creditsPerOperation?.toString() || op.defaultCredits.toString());
  };

  const title = t('screens.pricing.byOperation');

  return (
    <Box>
      <SectionHeader
        left={<PageTitle title={t('menu.pricing')} subtitle={t('screens.pricing.subtitle')} />}
        help={{ label: t('screens.pricing.help'), onClick: () => setHelpOpen(true) }}
      />
      <PricingHelp open={helpOpen} onClose={() => setHelpOpen(false)} />

      <Box sx={{ pt: 1, pb: 2 }}>
        <CostCenterSelect value={selectedCC} onChange={(id) => { setSelectedCC(id); setEditingId(null); }} />
      </Box>

      {!selectedCC ? (
        <Card>
          <DatagridEmpty title={t('screens.billing.pickCostCenter')} hint={t('screens.pricing.pickHint')} />
        </Card>
      ) : (
        <Box component="section" aria-labelledby="pricing-table">
          <BlockHeading id="pricing-table" title={title} subtitle={t('screens.pricing.tableScope')} />
          <DataTable label={title}>
            <TableHead>
              <TableRow>
                <TableCell>{t('screens.pricing.operationType')}</TableCell>
                <TableCell align="right">{t('screens.pricing.creditsPerOperation')}</TableCell>
                <TableCell sx={STATE_COLUMN}>{t('screens.pricing.state')}</TableCell>
                <TableCell align="right" aria-label={t('shell.rowActions.more')} />
              </TableRow>
            </TableHead>
            <TableBody>
              {OPERATION_TYPES.map(op => {
                const pricing = getPricing(op.id);
                const isEditing = editingId === op.id;
                const name = t(`screens.pricing.operations.${op.name}.name`);

                return (
                  <TableRow key={op.id}>
                    <TableCell>
                      <Box>{name}</Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('screens.pricing.defaultValue', { smart_count: op.defaultCredits, count: fmt.count(op.defaultCredits) })}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {isEditing ? (
                        <TextField
                          type="number"
                          size="small"
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          inputProps={{ step: 0.1, min: 0, 'aria-label': t('screens.pricing.creditsFor', { name }) }}
                          sx={{ width: 120 }}
                          autoFocus
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleSave(op.id);
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                        />
                      ) : pricing ? (
                        fmt.count(pricing.creditsPerOperation)
                      ) : (
                        <Typography variant="body2" component="span" color="text.secondary">
                          {t('screens.pricing.defaultShort', { count: fmt.count(op.defaultCredits) })}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={STATE_COLUMN}>
                      {pricing
                        ? <Chip label={t('screens.pricing.custom')} size="small" />
                        : <Typography variant="body2" component="span" color="text.secondary">{t('screens.pricing.default')}</Typography>}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {isEditing ? (
                        <>
                          <Tooltip title={t('ra.action.save')}>
                            <IconButton onClick={() => handleSave(op.id)} aria-label={t('ra.action.save')}>
                              <CheckRounded fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('ra.action.cancel')}>
                            <IconButton onClick={() => setEditingId(null)} aria-label={t('ra.action.cancel')}>
                              <CloseRounded fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <RowActionsMenu
                          actions={[
                            {
                              key: 'edit',
                              label: t('screens.pricing.edit'),
                              icon: <EditIcon fontSize="small" />,
                              onClick: () => startEdit(op),
                            },
                            {
                              key: 'restore',
                              label: t('screens.pricing.restore'),
                              icon: <RestoreIcon fontSize="small" />,
                              color: 'error',
                              dividerBefore: true,
                              hidden: !pricing,
                              onClick: () => pricing && setRestoring({ pricing, op }),
                            },
                          ]}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </DataTable>
        </Box>
      )}

      <Dialog open={Boolean(restoring)} onClose={() => setRestoring(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('screens.pricing.restoreTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {restoring && t('screens.pricing.restoreBody', {
              name: t(`screens.pricing.operations.${restoring.op.name}.name`),
              smart_count: restoring.op.defaultCredits,
              count: fmt.count(restoring.op.defaultCredits),
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setRestoring(null)}>{t('ra.action.cancel')}</Button>
          <Button variant="contained" color="error" onClick={() => restoring && handleRestore(restoring.pricing)}>
            {t('screens.pricing.restoreConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
